import { useFeatureFlag } from '@src/hooks';
import { CURRENT_USER_SENTINEL } from '@src/hooks/filters/useUsersFilter';
import { LookbackOption, type PersistedDefaultTabProps } from '@talos/kyoko';
import { useMemo } from 'react';
import type { OrderTabProps } from '../../../types/LayoutConfig';
import type { OrderBlotterDefaultColumn } from './orderColumnIds';
import type { BlotterTableOrderFilter } from './useOrderFilter';

export const OPEN_ORDERS_TAB_ID = 'open-orders';
export const RECENT_ORDERS_TAB_ID = 'recent-orders';
export const MY_ORDERS_TAB_ID = 'my-orders';

// Not excluding the recent orders tab here as it supports filtering
export const ORDER_TABS_EXCLUDED_IDS = [OPEN_ORDERS_TAB_ID];

interface OrderTabDefaults {
  tabs: PersistedDefaultTabProps<OrderTabProps>[];
  newTabDefaults: {
    defaultFilter: BlotterTableOrderFilter;
    defaultColumns: OrderBlotterDefaultColumn[];
  };
  openOrdersDefaultFilter: BlotterTableOrderFilter;
}

export function useDefaultOrderTabs(): OrderTabDefaults {
  const { enableArchiveOrders } = useFeatureFlag();

  return useMemo(() => {
    const openOrdersDefaultFilter: BlotterTableOrderFilter = {
      ShowArchivedOrders: !enableArchiveOrders,
    };

    const newTabDefaults = {
      defaultFilter: {
        _start: LookbackOption.Past24Hours,
        ShowArchivedOrders: !enableArchiveOrders,
      } satisfies BlotterTableOrderFilter,
      defaultColumns: [] satisfies OrderBlotterDefaultColumn[],
    };

    const tabs: PersistedDefaultTabProps<OrderTabProps>[] = [
      {
        label: 'Open',
        id: OPEN_ORDERS_TAB_ID,
        closable: false,
        editable: false,
        defaultFilter: openOrdersDefaultFilter,
        defaultColumns: [],
      },
      {
        label: 'Recent',
        id: RECENT_ORDERS_TAB_ID,
        closable: true,
        editable: true,
        defaultFilter: {
          _start: LookbackOption.Past24Hours,
          ShowArchivedOrders: !enableArchiveOrders,
        },
        defaultColumns: [],
      },
      {
        label: 'My Orders',
        id: MY_ORDERS_TAB_ID,
        closable: true,
        editable: true,
        defaultFilter: {
          Users: [CURRENT_USER_SENTINEL],
          _start: LookbackOption.Past24Hours,
          ShowArchivedOrders: !enableArchiveOrders,
        },
        defaultColumns: [],
      },
    ];

    return { tabs, newTabDefaults, openOrdersDefaultFilter };
  }, [enableArchiveOrders]);
}
